import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowRight, ChevronRight } from 'lucide-react';
import Icon from '../components/Icon';
import Reveal from '../components/Reveal';
import SectorNav from '../components/SectorNav';
import useSeo from '../hooks/useSeo';
import { IMAGES, SECTOR_PAGES } from '../mock';

const GoldOutlineBtn = ({ to, children }) => (
  <Link
    to={to}
    data-testid="sector-outline-btn"
    className="inline-flex items-center gap-3 font-label transition-colors"
    style={{ border: '1px solid var(--gold)', color: 'var(--gold)', padding: '12px 22px', fontWeight: 600, letterSpacing: '0.08em', fontSize: '0.8rem' }}
  >
    {children} <ArrowRight size={16} />
  </Link>
);

const GoldFillBtn = ({ to, children, className = '' }) => (
  <Link
    to={to}
    data-testid="sector-fill-btn"
    className={`inline-flex items-center justify-center gap-3 font-label transition-transform hover:-translate-y-0.5 ${className}`}
    style={{ background: 'var(--gold)', color: 'var(--navy)', padding: '13px 24px', fontWeight: 700, letterSpacing: '0.08em', fontSize: '0.8rem' }}
  >
    {children} <ArrowRight size={16} />
  </Link>
);

const RuleHeading = ({ children, testid }) => (
  <div className="flex items-center gap-4 md:gap-6" data-testid={testid}>
    <span className="flex-1 h-px" style={{ background: 'rgba(200,162,76,0.55)' }} />
    <h2 className="section-title text-center" style={{ fontSize: 'clamp(1.15rem, 2.6vw, 1.7rem)', margin: 0 }}>{children}</h2>
    <span className="flex-1 h-px" style={{ background: 'rgba(200,162,76,0.55)' }} />
  </div>
);

const SectorPage = ({ slug: fixedSlug }) => {
  const params = useParams();
  const slug = fixedSlug || params.slug;
  const c = SECTOR_PAGES[slug];
  const seoName = c?.seoName;
  useSeo({
    title: seoName ? `${seoName} Investments | Duffy Investment Group` : undefined,
    description: seoName ? `Explore Duffy Investment Group's interests and experience in ${seoName}, including relevant investment opportunities, businesses and industry developments.` : undefined,
    keywords: seoName ? `Duffy Investment Group, ${seoName}, ${seoName} investments, ${seoName} investment opportunities, private investment` : undefined,
  });
  if (!c) return <Navigate to="/our-investments" replace />;

  const DetailedStrip = () => (
    <section style={{ background: 'var(--navy-deep)' }}>
      <div className="dig-container py-12 md:py-14">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-9 gap-x-4">
          {c.strip.items.map((it, i) => (
            <div key={it.title} className={`flex flex-col items-center text-center md:items-start md:text-left px-2 md:px-6 ${i > 0 ? 'md:border-l' : ''}`} style={{ borderColor: 'rgba(255,255,255,0.12)' }}>
              <Icon name={it.icon} size={34} strokeWidth={1.2} style={{ color: 'var(--gold)' }} />
              <h3 className="font-serif-dig gold-text mt-3" style={{ fontWeight: 700, fontSize: '0.95rem', letterSpacing: '0.04em' }}>{it.title}</h3>
              <p className="font-body mt-2" style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.82rem', lineHeight: 1.55 }}>{it.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  const CompactStrip = () => (
    <section style={{ background: 'var(--navy-deep)' }}>
      <div className="dig-container py-8 md:py-9">
        <div className="grid grid-cols-4">
          {c.strip.items.map((it, i) => (
            <div key={it.l1} className={`flex flex-col items-center text-center md:flex-row md:text-left gap-2 md:gap-3 px-1 md:px-6 ${i > 0 ? 'border-l' : ''}`} style={{ borderColor: 'rgba(255,255,255,0.14)' }}>
              <Icon name={it.icon} size={28} strokeWidth={1.2} style={{ color: 'var(--gold)', flexShrink: 0 }} />
              <p className="font-label" style={{ color: 'var(--gold)', fontWeight: 600, letterSpacing: '0.06em', fontSize: '0.62rem', lineHeight: 1.3 }}>
                <span className="block">{it.l1}</span><span className="block">{it.l2}</span>
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  const Band = () => (
    <section className="bg-navy">
      <div className="dig-container py-9 md:py-11">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-5 md:gap-8">
          <Icon name="Globe" size={46} strokeWidth={1} style={{ color: 'var(--gold)', flexShrink: 0 }} />
          <div className="flex-1">
            <h3 className="font-serif-dig gold-text" style={{ fontSize: 'clamp(1.1rem, 3.5vw, 1.35rem)', fontWeight: 700, lineHeight: 1.2 }}>GLOBAL VISION. LOCAL KNOWLEDGE.</h3>
            <p className="font-body mt-1.5" style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.92rem', lineHeight: 1.5 }}>
              Partnering with exceptional {c.bandSub || 'businesses'} to build, grow and create long-term value.
            </p>
          </div>
          <GoldFillBtn to="/our-investments" className="w-full md:w-auto shrink-0">{c.bandButton}</GoldFillBtn>
        </div>
      </div>
    </section>
  );

  return (
    <div>
      {/* ===================== HERO ===================== */}
      <section className="relative bg-navy-grad overflow-hidden">
        <div className="absolute inset-0 lg:left-[42%]">
          <img src={IMAGES[c.heroImg]} alt={c.titleWhite} className="w-full h-full object-cover" />
          <div className="absolute inset-0 lg:hidden" style={{ background: 'linear-gradient(180deg, rgba(7,19,34,0.45) 0%, rgba(7,19,34,0.62) 45%, rgba(7,19,34,0.96) 100%)' }} />
          <div className="absolute inset-0 hidden lg:block" style={{ background: 'linear-gradient(90deg, rgba(10,26,48,1) 0%, rgba(10,26,48,0.85) 12%, rgba(10,26,48,0.3) 34%, rgba(10,26,48,0) 50%)' }} />
        </div>
        <div className="relative dig-container">
          <div className="flex items-center" style={{ minHeight: 540 }}>
            <div className="pt-14 pb-14 lg:py-24 max-w-xl">
              <Reveal>
                <p className="eyebrow gold-text" style={{ letterSpacing: '0.22em' }}>OUR INVESTMENTS</p>
                <h1 className="font-serif-dig text-white" style={{ fontSize: 'clamp(2rem, 6vw, 3.3rem)', fontWeight: 700, lineHeight: 1.06, marginTop: 10 }}>{c.titleWhite}</h1>
                <div className="font-serif-dig gold-text" style={{ fontSize: 'clamp(1.4rem, 4.4vw, 2.3rem)', fontWeight: 700, lineHeight: 1.1, marginTop: 2 }}>
                  {c.titleGold.map((l) => (<span key={l} className="block">{l}</span>))}
                </div>
                <span className="gold-rule-lg block my-6" />
              </Reveal>
              <Reveal delay={120}>
                <p className="font-body text-white" style={{ fontSize: '1.1rem', lineHeight: 1.6, fontWeight: 500 }}>{c.lead}</p>
                <p className="font-body mt-4" style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.98rem', lineHeight: 1.7 }}>{c.sub}</p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTOR NAV (bottom of hero) ===== */}
      <SectorNav />

      {/* ===================== TWO-COLUMN ===================== */}
      <section className="bg-cream">
        <div className="dig-container py-16 md:py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <Reveal>
              <h2 className="font-serif-dig gold-text" style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)', fontWeight: 700, lineHeight: 1.25 }}>{c.two.heading}</h2>
              {c.two.paras.map((p, i) => (
                <p key={i} className="font-body mt-5" style={{ color: i === 0 ? 'var(--ink)' : 'var(--muted)', lineHeight: 1.75, fontSize: '1.02rem' }}>{p}</p>
              ))}
              {c.two.button && (<div className="mt-8"><GoldOutlineBtn to={c.two.button.to}>{c.two.button.label}</GoldOutlineBtn></div>)}
            </Reveal>
            <Reveal delay={140}>
              <div className="lg:border-l lg:pl-12" style={{ borderColor: 'rgba(0,0,0,0.12)' }}>
                <img src={IMAGES[c.two.img]} alt={c.two.heading} className="w-full object-cover" style={{ maxHeight: 430 }} />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===================== FOCUS ===================== */}
      {c.focus && (
        <section className="bg-cream-2">
          <div className="dig-container py-16 md:py-20">
            <Reveal>
              <div className="hidden md:block"><RuleHeading testid="focus-heading">{c.focus.title}</RuleHeading></div>
              <div className="md:hidden"><RuleHeading testid="focus-heading-mobile">{c.focus.mobileTitle || c.focus.title}</RuleHeading></div>
            </Reveal>

            {/* desktop */}
            <div className="hidden md:grid mt-14 items-stretch" style={{ gridTemplateColumns: `repeat(${c.focus.cols.length + (c.focus.img ? 1 : 0)}, minmax(0,1fr))` }}>
              {c.focus.cols.map((col, i) => (
                <div key={col.title} className="px-6 text-center flex flex-col items-center" style={{ borderLeft: i > 0 ? '1px solid rgba(0,0,0,0.1)' : 'none' }}>
                  {c.focus.circleIcons ? (
                    <div className="grid place-items-center rounded-full" style={{ width: 70, height: 70, background: 'var(--navy)' }}>
                      <Icon name={col.icon} size={32} strokeWidth={1.4} style={{ color: 'var(--gold)' }} />
                    </div>
                  ) : (
                    <Icon name={col.icon} size={46} strokeWidth={1.3} style={{ color: 'var(--gold)' }} />
                  )}
                  <h3 className="font-serif-dig navy-text mt-4" style={{ fontWeight: 700, fontSize: '1rem', letterSpacing: '0.02em', lineHeight: 1.25 }}>{col.title}</h3>
                  <p className="font-body mt-3" style={{ color: 'var(--muted)', fontSize: '0.9rem', lineHeight: 1.6 }}>{col.text}</p>
                </div>
              ))}
              {c.focus.img && (
                <div className="overflow-hidden self-stretch" style={{ minHeight: 220 }}>
                  <img src={IMAGES[c.focus.img]} alt="" className="w-full h-full object-cover" />
                </div>
              )}
            </div>

            {/* mobile */}
            <div className="md:hidden mt-9 space-y-3">
              {c.focus.cols.map((col) => (
                <div key={col.title} className="flex items-center gap-4" style={{ borderBottom: '1px solid rgba(0,0,0,0.08)', paddingBottom: '0.9rem' }}>
                  <div className="grid place-items-center rounded-full shrink-0" style={{ width: 52, height: 52, border: '1.5px solid var(--gold)' }}>
                    <Icon name={col.icon} size={22} strokeWidth={1.4} style={{ color: 'var(--gold)' }} />
                  </div>
                  <div className="flex-1">
                    <p className="font-serif-dig navy-text" style={{ fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.2 }}>{col.title}</p>
                    {col.short && <p className="font-body" style={{ color: 'var(--muted)', fontSize: '0.78rem', marginTop: 2 }}>{col.short}</p>}
                  </div>
                  <ChevronRight size={20} style={{ color: 'var(--gold)', flexShrink: 0 }} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===================== FEATURE (e.g. PRWeb) ===================== */}
      {c.feature && (
        <section className="bg-cream-2">
          <div className="dig-container py-16 md:py-20">
            <Reveal>
              <RuleHeading testid="feature-heading">{c.feature.title}</RuleHeading>
            </Reveal>
            <div className="grid md:grid-cols-[300px_1fr] gap-8 md:gap-12 items-center mt-10 max-w-5xl mx-auto">
              <div className="text-center md:border-r md:pr-10" style={{ borderColor: 'rgba(0,0,0,0.1)' }}>
                {c.feature.logo && IMAGES[c.feature.logo] ? (
                  <img src={IMAGES[c.feature.logo]} alt={c.feature.logoText} className="mx-auto w-full" style={{ maxWidth: 265 }} />
                ) : (
                  <div className="inline-block">
                    <span className="font-serif-dig" style={{ fontSize: '2.4rem', fontWeight: 800 }}>
                      <span style={{ color: '#e23b3b' }}>PR</span><span style={{ color: '#2c5fb3' }}>Web</span>
                    </span>
                    <p className="font-body" style={{ color: 'var(--muted)', fontSize: '0.72rem', letterSpacing: '0.05em', marginTop: 2 }}>{c.feature.logoTag}</p>
                  </div>
                )}
              </div>
              <div>
                {c.feature.paras.map((p, i) => (
                  <p key={i} className={i === 0 ? 'font-body' : 'font-body mt-4'} style={{ color: i === 0 ? 'var(--ink)' : 'var(--muted)', lineHeight: 1.7, fontSize: '1rem', fontWeight: i === 0 ? 600 : 400 }}>{p}</p>
                ))}
                <div className="mt-6"><GoldOutlineBtn to={c.feature.button.to || '/contact'}>{c.feature.button.label}</GoldOutlineBtn></div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ===================== STRIP + BAND (order varies) ===================== */}
      {c.strip.style === 'detailed' ? (
        <>
          <DetailedStrip />
          <Band />
        </>
      ) : (
        <>
          <Band />
          <CompactStrip />
        </>
      )}
    </div>
  );
};

export default SectorPage;
